/**
 * Arbre de décision « régime maladie / retraite » (demande d'entrée).
 * Les codes régime correspondent aux entrées Paramètres (GPALP COTIS) : URS, URS2, CRP, CRPUR.
 */

export const regimeAideChoixKey = "__AIDE_REGIME__";

/** Codes stockés en base */
const regimeCode = {
    RegimeGeneral: "URS",
    NotariatDomTom: "URS2",
    Crpcen: "CRP",
    MixteCrpcenUrssaf: "CRPUR"
};

export interface DecisionQuestionNode {
    id: string;
    type: "question";
    text: string;
    answers: { label: string; nextId: string }[];
}

export interface DecisionLeafNode {
    id: string;
    type: "leaf";
    regimeCode: string;
}

export type DecisionNode = DecisionQuestionNode | DecisionLeafNode;

/** Id de départ de l'arbre. */
export const startNodeId = "q1";

/**
 * @description Crée un noeud de question.
 * @param id L'ID du noeud.
 * @param text Le texte de la question.
 * @param answers Les réponses possibles.
 * @returns Un noeud de question.
 */
function q(id: string, text: string, answers: { label: string; nextId: string }[]): DecisionQuestionNode {
    return { id, type: "question", text, answers };
}

/**
 * @description Crée un noeud de feuille.
 * @param id L'ID du noeud.
 * @param regimeCode Code GPALP (ex. URS, CRP, CRPUR).
 * @returns Un noeud de feuille.
 */
function l(id: string, regimeCode: string): DecisionLeafNode {
    return { id, type: "leaf", regimeCode };
}

/** Questions (q1 … q8) */
const questions: Record<string, DecisionQuestionNode> = {
    q1: q("q1", "Où se situe l'étude notariale ?", [
        { label: "Alsace-Moselle", nextId: "l_general" },
        { label: "DOM-TOM", nextId: "l_domtom" },
        { label: "France métropolitaine (hors Alsace-Moselle)", nextId: "q2" }
    ]),
    q2: q("q2", "Quelle est votre situation ?", [
        { label: "Première activité dans le notariat", nextId: "l_mixte" },
        { label: "Retraité affilié à la CRPCEN", nextId: "q3" },
        { label: "Ancien salarié du notariat", nextId: "q4" }
    ]),
    q3: q("q3", "Quand reprenez-vous une activité dans le notariat en tant que cumul emploi-retraite ?", [
        { label: "Après un mois à compter du départ en retraite", nextId: "l_mixte" },
        { label: "Dans le mois suivant le départ en retraite", nextId: "l_crpcen" }
    ]),
    q4: q("q4", "Quand le dernier contrat a-t-il été rompu ou suspendu (CDD / CDI) ?", [
        { label: "Avant le 01/09/2013", nextId: "l_mixte" },
        { label: "Après le 01/09/2023", nextId: "q5" },
        { label: "Entre le 01/09/2013 et avant le 31/08/2023", nextId: "q6" }
    ]),
    q5: q("q5", "Qui est à l'origine de la rupture du contrat ?", [
        { label: "L'employeur", nextId: "q7" },
        { label: "Le salarié (démission, fin de CDD, rupture conventionnelle)", nextId: "q8" },
        { label: "Autre situation (ex : chômage indemnisé)", nextId: "l_crpcen" }
    ]),
    q6: q("q6", "Quand reprenez-vous un contrat dans le notariat à compter de la date de rupture ou de suspension ?", [
        { label: "Dans un délai maximum de 10 ans", nextId: "l_crpcen" },
        { label: "Dans un délai supérieur à 10 ans", nextId: "l_mixte" }
    ]),
    q7: q("q7", "Quand reprenez-vous un contrat dans le notariat ?", [
        { label: "Après 1 an à compter de la date de rupture ou suspension du contrat", nextId: "l_mixte" },
        { label: "Dans l'année suivant la rupture ou suspension avec affiliation auprès d'un autre régime", nextId: "l_mixte" },
        { label: "Dans l'année suivant la rupture ou suspension sans affiliation auprès d'un autre régime", nextId: "l_crpcen" },
    ]),
    q8: q("q8", "Quand reprenez-vous un contrat dans le notariat ?", [
        { label: "Après un mois à compter de la date de rupture ou suspension du dernier contrat", nextId: "l_mixte" },
        { label: "Dans le mois suivant la rupture ou suspension du dernier contrat, mais avec affiliation auprès d'un autre régime", nextId: "l_mixte" },
        { label: "Dans le mois suivant la rupture sans autre régime", nextId: "l_crpcen" }
    ])
};

// --- Résultats ---

const results: Record<string, DecisionLeafNode> = {
    l_general: l("l_general", regimeCode.RegimeGeneral),
    l_domtom: l("l_domtom", regimeCode.NotariatDomTom),
    l_mixte: l("l_mixte", regimeCode.MixteCrpcenUrssaf),
    l_crpcen: l("l_crpcen", regimeCode.Crpcen),
};

// --- Index complet (questions + résultats) ---

export const decisionNodes: Record<string, DecisionNode> = { ...questions, ...results };

/**
 * @description Retourne le noeud correspondant à l'ID.
 * @param id L'ID du noeud.
 * @returns Le noeud correspondant à l'ID.
 */
export function getDecisionNode(id: string): DecisionNode | undefined {
    return decisionNodes[id];
}



// WEBPACK FOOTER //
// ./src/components/entreeSalarie/modalRegimeDecision/regimeDecisionEntree.tree.ts